/**
 * masterGanttAssignmentMerge.ts
 * BUG-2026-0194 — Staff/People aggregation helpers for the Master Gantt.
 *
 * Why this exists
 * ----------------
 * `EmployeeAssignment` rows are WEEKLY (one row per person·task·WeekStart), so a
 * three-week task produced three separate one-week blocks per person, and a
 * reviewer/approver attached to every task of a job produced one row per task.
 * The lab asked for:
 *   - one bar per person (with a drill-down into their tasks), and
 *   - task bars that are not chopped into weeks.
 *
 * This module is the pure data-shaping half shared by BOTH group-by layouts
 * (masterGanttProjectRows.ts and masterGanttMachineRows.ts):
 *
 *   mergeAssignmentsByTask()   weekly rows → one MergedAssignment per (person, task)
 *   groupAssignmentsByPerson() merged tasks → one PersonTaskGroup per person
 *   buildMergedAssignmentPoint() → the Highcharts bar for a merged task, built by
 *                              the SAME buildAssignmentPoint used everywhere else
 *                              so its id ("assign-{firstKey}") / custom keys keep
 *                              conflict tagging, deep-links and the drawer working.
 *                              Every folded weekly key rides along in
 *                              `custom.assignmentKeys` (multi-select + drawer span).
 *   label helpers              "· N tasks · D days" / "· S%" suffixes.
 *
 * Roles (reviewer / approver / mentor) are NOT persisted anywhere — the Scenario
 * Planner only uses them as a percent shortcut — so the only stored hint is the
 * EmployeeSourceAllocation share (`sharePercent`, BUG-2026-0196). Person rows
 * print it when it is below 100 so a "10%" reviewer is distinguishable from a
 * full-time technician.
 *
 * No DOM, no theme, no Highcharts runtime — unit-testable with plain objects.
 */

import type {
  MasterGanttEmployeeAssignment,
  MasterGanttMachineBooking,
} from '../../types/schedule.js';

import {
  buildAssignmentPoint,
  addDays,
  toMs,
  DAY_MS,
  type GanttPoint,
  type GanttColorTokens,
} from './masterGanttRowBuilder.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One bar per (person, task): every weekly row of that pair folded together. */
export interface MergedAssignment {
  /** Earliest weekly row — its assignmentKey becomes the bar id. */
  primary: MasterGanttEmployeeAssignment;
  /** Every weekly row folded into this bar (earliest first, incl. primary). */
  rows: MasterGanttEmployeeAssignment[];
  /** assignmentKey of every folded row (earliest first). */
  assignmentKeys: string[];
  userKey: string;
  displayName: string;
  taskName: string | null;
  scheduleTaskKey: string | null;
  /** Inclusive 'YYYY-MM-DD' span across all folded weeks. */
  startDate: string;
  endDate: string;
  /** 'Committed' only when every folded week is Committed; else 'Tentative'. */
  status: string;
  source: string;
  /** First known share across the folded weeks; null when none is stored. */
  sharePercent: number | null;
}

/** One person on a work order (or machine × work order): their merged tasks. */
export interface PersonTaskGroup {
  userKey: string;
  displayName: string;
  /** Merged task bars, ordered by start date then task name. */
  tasks: MergedAssignment[];
  /** Inclusive envelope across every task. */
  startDate: string;
  endDate: string;
  /** Lowest / highest known share across the tasks; both null when unknown. */
  shareMin: number | null;
  shareMax: number | null;
}

/** Minimal inclusive date range — bookings and merged assignments both fit. */
export interface DateSpan {
  startDate: string;
  endDate: string;
}

// ---------------------------------------------------------------------------
// Spans
// ---------------------------------------------------------------------------

/** Inclusive span of one weekly row (explicit dates, else its Sat–Fri week). */
export function assignmentSpan(ea: MasterGanttEmployeeAssignment): DateSpan {
  return {
    startDate: ea.startDate ?? ea.weekStart,
    endDate: ea.endDate ?? addDays(ea.weekStart, 6),
  };
}

/**
 * Number of DISTINCT calendar days covered by a set of inclusive ranges
 * (overlaps counted once). Purely a display aggregate for the "· D days"
 * suffix — NOT a working-day / capacity figure (those live in
 * backend/src/calculation/schedulingCalculations.ts).
 */
export function coveredDays(spans: DateSpan[]): number {
  const ranges = spans
    .map((s) => ({ start: toMs(s.startDate), end: toMs(s.endDate) }))
    .filter((r): r is { start: number; end: number } => r.start !== undefined && r.end !== undefined && r.end >= r.start)
    .sort((a, b) => a.start - b.start);

  let total = 0;
  let curStart: number | null = null;
  let curEnd: number | null = null;
  for (const r of ranges) {
    if (curStart === null || curEnd === null || r.start > curEnd + DAY_MS) {
      if (curStart !== null && curEnd !== null) total += (curEnd - curStart) / DAY_MS + 1;
      curStart = r.start;
      curEnd = r.end;
    } else if (r.end > curEnd) {
      curEnd = r.end;
    }
  }
  if (curStart !== null && curEnd !== null) total += (curEnd - curStart) / DAY_MS + 1;
  return Math.round(total);
}

function minIso(a: string, b: string): string { return a < b ? a : b; }
function maxIso(a: string, b: string): string { return a > b ? a : b; }

function knownShare(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

// ---------------------------------------------------------------------------
// Merge weekly rows → one per (person, task)
// ---------------------------------------------------------------------------

/**
 * Fold weekly EmployeeAssignment rows into one entry per (userKey, task).
 * Rows without a scheduleTaskKey can't be matched to siblings and stay as
 * their own single-row entry. Output is ordered by person name, then start
 * date, then task name (deterministic for stable treegrid ids).
 */
export function mergeAssignmentsByTask(
  assignments: MasterGanttEmployeeAssignment[],
): MergedAssignment[] {
  const buckets = new Map<string, MasterGanttEmployeeAssignment[]>();
  for (const ea of assignments) {
    const key = `${ea.userKey}|${ea.scheduleTaskKey ?? `row:${ea.assignmentKey}`}`;
    const list = buckets.get(key);
    if (list) list.push(ea);
    else buckets.set(key, [ea]);
  }

  const merged: MergedAssignment[] = [];
  for (const rows of buckets.values()) {
    rows.sort((a, b) => {
      const sa = assignmentSpan(a).startDate;
      const sb = assignmentSpan(b).startDate;
      return sa < sb ? -1 : sa > sb ? 1 : a.assignmentKey.localeCompare(b.assignmentKey);
    });
    const primary = rows[0];
    let startDate = assignmentSpan(primary).startDate;
    let endDate = assignmentSpan(primary).endDate;
    let allCommitted = true;
    let share: number | null = null;
    for (const r of rows) {
      const span = assignmentSpan(r);
      startDate = minIso(startDate, span.startDate);
      endDate = maxIso(endDate, span.endDate);
      if (r.status !== 'Committed') allCommitted = false;
      if (share === null) share = knownShare(r.sharePercent);
    }
    merged.push({
      primary,
      rows,
      assignmentKeys: rows.map((r) => r.assignmentKey),
      userKey: primary.userKey,
      displayName: primary.displayName,
      taskName: primary.taskName,
      scheduleTaskKey: primary.scheduleTaskKey,
      startDate,
      endDate,
      status: allCommitted ? 'Committed' : 'Tentative',
      source: primary.source,
      sharePercent: share,
    });
  }

  merged.sort((a, b) => {
    const byName = a.displayName.localeCompare(b.displayName);
    if (byName !== 0) return byName;
    if (a.startDate !== b.startDate) return a.startDate < b.startDate ? -1 : 1;
    return (a.taskName ?? '').localeCompare(b.taskName ?? '');
  });
  return merged;
}

// ---------------------------------------------------------------------------
// Group merged tasks → one per person
// ---------------------------------------------------------------------------

/** One PersonTaskGroup per userKey (ordered by display name), tasks merged by task. */
export function groupAssignmentsByPerson(
  assignments: MasterGanttEmployeeAssignment[],
): PersonTaskGroup[] {
  const byUser = new Map<string, PersonTaskGroup>();
  for (const task of mergeAssignmentsByTask(assignments)) {
    let group = byUser.get(task.userKey);
    if (!group) {
      group = {
        userKey: task.userKey,
        displayName: task.displayName,
        tasks: [],
        startDate: task.startDate,
        endDate: task.endDate,
        shareMin: null,
        shareMax: null,
      };
      byUser.set(task.userKey, group);
    }
    group.tasks.push(task);
    group.startDate = minIso(group.startDate, task.startDate);
    group.endDate = maxIso(group.endDate, task.endDate);
    if (task.sharePercent !== null) {
      group.shareMin = group.shareMin === null ? task.sharePercent : Math.min(group.shareMin, task.sharePercent);
      group.shareMax = group.shareMax === null ? task.sharePercent : Math.max(group.shareMax, task.sharePercent);
    }
  }
  return Array.from(byUser.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
}

// ---------------------------------------------------------------------------
// Bar builder for a merged task
// ---------------------------------------------------------------------------

/**
 * The Highcharts bar for a merged (person, task) — built via the shared
 * buildAssignmentPoint on the EARLIEST weekly row with the folded span, so the
 * bar id is "assign-{firstKey}" and `custom` keeps assignmentKey / userKey /
 * scheduleTaskKey exactly as a single-week bar would. Adds:
 *   custom.assignmentKeys — every folded weekly key (multi-select, drawer span)
 *   custom.weekCount      — how many weekly rows were folded (tooltip)
 */
export function buildMergedAssignmentPoint(
  merged: MergedAssignment,
  parentId: string,
  colors: GanttColorTokens,
  woNumber?: string | null,
): GanttPoint {
  const synthesized: MasterGanttEmployeeAssignment = {
    ...merged.primary,
    startDate: merged.startDate,
    endDate: merged.endDate,
    status: merged.status,
    sharePercent: merged.sharePercent,
  };
  const point = buildAssignmentPoint(synthesized, parentId, colors, woNumber);
  point.custom = {
    ...(point.custom ?? {}),
    assignmentKeys: merged.assignmentKeys,
    weekCount: merged.rows.length,
  };
  return point;
}

// ---------------------------------------------------------------------------
// Label helpers
// ---------------------------------------------------------------------------

/** "1 task" / "4 tasks". */
export function taskCountLabel(n: number): string {
  return `${n} task${n === 1 ? '' : 's'}`;
}

/** "1 day" / "6 days". */
export function dayCountLabel(n: number): string {
  return `${n} day${n === 1 ? '' : 's'}`;
}

/**
 * "· 10%" when a below-100 share is known; "· 5–25%" when tasks disagree;
 * "" for full-time / unknown (100 is presence — printing it would be noise).
 */
export function shareSuffix(shareMin: number | null, shareMax: number | null): string {
  if (shareMin === null || shareMax === null) return '';
  if (shareMin >= 100 && shareMax >= 100) return '';
  return shareMin === shareMax ? ` · ${shareMin}%` : ` · ${shareMin}–${shareMax}%`;
}

/** Person summary row label: "Shawn Hegger · 6 tasks · 10%". */
export function personRowLabel(group: PersonTaskGroup): string {
  return `${group.displayName} · ${taskCountLabel(group.tasks.length)}${shareSuffix(group.shareMin, group.shareMax)}`;
}

/** Machine summary row label: "RK-SAW — Cutting saw · 4 tasks · 6 days". */
export function machineRowLabel(
  machineCode: string,
  machineName: string | null | undefined,
  bookings: MasterGanttMachineBooking[],
): string {
  const head = machineName ? `${machineCode} — ${machineName}` : machineCode;
  return `${head} · ${taskCountLabel(bookings.length)} · ${dayCountLabel(coveredDays(bookings))}`;
}

/** Test-type (task name) row label: "Cutting (Preparation) · 4 tasks · 6 days". */
export function testTypeRowLabel(
  taskName: string,
  bookings: MasterGanttMachineBooking[],
): string {
  return `${taskName} · ${taskCountLabel(bookings.length)} · ${dayCountLabel(coveredDays(bookings))}`;
}

/** Display name for a booking's test type: task name, else event type, else source. */
export function bookingTestType(bk: MasterGanttMachineBooking): string {
  return bk.taskName ?? bk.eventType ?? bk.source ?? 'Other';
}

/** URL-ish slug for stable treegrid ids: "Cutting (Preparation)" → "cutting-preparation". */
export function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'other';
}
